import { useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Camera, X } from 'lucide-react'
import { PageHeader, Panel, Button, FormRow, Textarea, Banner, useToast } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { post } from '../../lib/api'
import { money } from '../../lib/format'
import { Icon } from '../../components/Icon'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { TierBadge, type Lead } from './ClientLeads'
import { DISPUTE_REASONS, WEEKLY_DISPUTE_REASONS } from './disputes'
import { isWeekly, tierOf, weeklyCost } from './weekly'
import type { Summary } from './ClientOverview'

/**
 * The dispute form, one page: which lead and what comes back if it is agreed,
 * then what was wrong (one tap), why, and an optional screenshot as proof.
 */
export default function ClientDispute() {
  const { id = '' } = useParams()
  const lead = useApi<{ lead: Lead }>(`/api/client/leads/${encodeURIComponent(id)}`)
  const summary = useApi<Summary>('/api/client/summary')
  const toast = useToast()
  const go = useNavigate()
  const [category, setCategory] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [image, setImage] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const file = useRef<HTMLInputElement>(null)

  if (lead.error) return <ErrorBlock error={lead.error} />
  if (!lead.data) return <LoadingBlock />
  const l = lead.data.lead
  const orgName = summary.data?.org.name || 'Handpicked'
  const tz = summary.data?.org.timezone
  const when = (d: string | null) => d
    ? new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(d))
    : ''
  const back = `/portal/leads/${encodeURIComponent(l.id)}`
  // What a refund puts back: the credit the lead used, at normal prices, and anything it added to the bill.
  const weekly = isWeekly(l)
  const reasons = weekly ? WEEKLY_DISPUTE_REASONS : DISPUTE_REASONS
  const refundCents = weekly ? (weeklyCost(l)?.amount ?? 0) : l.prepaid_credit_cents + l.overage_cents || l.billed_cents || 0
  const picked = reasons.find((r) => r.key === category)
  const tier = tierOf(l)

  const pick = (f: File | undefined) => {
    setErr(null)
    if (!f) return
    if (!/^image\/(png|jpeg|webp)$/.test(f.type)) return setErr('Use a PNG, JPEG or WebP screenshot')
    if (f.size > 5 * 1024 * 1024) return setErr('That screenshot is over 5 MB')
    const r = new FileReader()
    r.onload = () => setImage(String(r.result))
    r.readAsDataURL(f)
  }
  const send = async () => {
    setErr(null)
    if (!category) return setErr('Pick what was wrong with this lead')
    if (reason.trim().length < 5) return setErr('Tell us a little more about what was wrong')
    setBusy(true)
    try {
      await post(`/api/client/leads/${encodeURIComponent(l.id)}/dispute`, { category, reason: reason.trim(), image: image || undefined })
      toast(`Dispute sent to ${orgName}`, 'pos')
      go('/portal/disputes')
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }

  const header = (
    <>
      <div style={{ marginBottom: 'var(--s-4)' }}><Link className="btn btn-ghost" to={back}><Icon of={ArrowLeft} /> Back to the lead</Link></div>
      <PageHeader title="Dispute a lead" subtitle={`Tell ${orgName} what was wrong. If they agree, the money goes back.`} />
    </>
  )
  if (!l.can_dispute) {
    return (
      <>
        {header}
        <Banner tone="neutral" title={l.dispute_status ? 'This lead has already been disputed' : l.grading ? 'Not charged yet' : 'The time to dispute this lead has passed'}>
          {l.dispute_status ? <Link to="/portal/disputes">See where it stands</Link> : l.grading ? 'You can dispute it once it has been graded and charged.' : <>Questions about it? Contact {orgName}.</>}
        </Banner>
      </>
    )
  }

  return (
    <>
      {header}
      <div className="dispute-form">
        <Panel>
          <div className="dispute-lead">
            <TierBadge name={tier.name} tierKey={tier.key} large />
            <div style={{ minWidth: 0 }}>
              <strong>{l.summary || 'Asked for a recommendation'}</strong>
              <span className="muted">{[when(l.sent_at), l.area_name || l.area_text].filter(Boolean).join(' · ')}</span>
            </div>
            <div className="dispute-amount">
              <span className="muted">{weekly ? 'Not billed if agreed' : 'Comes back if agreed'}</span>
              <strong className="num">{money(refundCents)}</strong>
              <span className="muted">{weekly ? 'or billed at the lower tier, for a tier that was too high'
                : [l.prepaid_credit_cents > 0 ? `${money(l.prepaid_credit_cents)} of credit` : '', l.overage_cents > 0 ? `${money(l.overage_cents)} off your bill` : ''].filter(Boolean).join(' and ') || 'off your bill'}</span>
            </div>
          </div>
          <p className="muted dispute-deadline">You can send this until {when(l.dispute_until)}.{weekly ? ` ${orgName} decides it before the invoice goes out.` : ''}</p>
        </Panel>

        <Panel>
          <fieldset className="dispute-step">
            <legend><span className="dispute-num">1</span> What was wrong?</legend>
            <div className="reason-picks" role="radiogroup" aria-label="What was wrong">
              {reasons.map((r) => (
                <button key={r.key} type="button" role="radio" aria-checked={category === r.key} className="reason-pick" onClick={() => { setCategory(r.key); setErr(null) }}>
                  <strong>{r.label}</strong><span className="muted">{r.hint}</span>
                </button>
              ))}
            </div>
          </fieldset>
        </Panel>

        <Panel>
          <fieldset className="dispute-step">
            <legend><span className="dispute-num">2</span> Tell us more</legend>
            <FormRow label={picked?.ask || 'What was wrong with it?'} htmlFor="dispute-reason" help="The more specific, the faster it gets sorted.">
              <Textarea id="dispute-reason" rows={4} value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} />
            </FormRow>
          </fieldset>
        </Panel>

        <Panel>
          <fieldset className="dispute-step">
            <legend><span className="dispute-num">3</span> Add proof <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></legend>
            <p className="muted" style={{ marginTop: 0 }}>A screenshot of a text, a call log, or anything that shows what happened.</p>
            <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = '' }} />
            {image ? (
              <div className="dispute-proof">
                <img src={image} alt="Your screenshot" />
                <Button variant="ghost" onClick={() => setImage(null)}><Icon of={X} size={15} /> Remove</Button>
              </div>
            ) : (
              <Button variant="secondary" onClick={() => file.current?.click()}><Icon of={Camera} size={15} /> Add a screenshot</Button>
            )}
          </fieldset>
        </Panel>

        {err && <Banner tone="neg" title="Not sent yet">{err}</Banner>}
        <div className="dispute-actions">
          <Button onClick={send} disabled={busy}>{busy ? 'Sending...' : 'Send dispute'}</Button>
          <Link className="btn btn-ghost" to={back}>Cancel</Link>
          <span className="muted" style={{ fontSize: 13 }}>{orgName} reviews it and you will see the answer on this lead and under Disputes.</span>
        </div>
      </div>
    </>
  )
}
